import React, { useEffect, useRef, useState } from "react";

/**
 * High-Performance Liquid CSS Glass Button
 * - Glass body: Apple-style frosted glass with SVG turbulence filter
 * - Shimmer overlay: Pure CSS dynamic gradient animation (replaces crash-prone WebGL)
 */
export function LiquidMetalButton({
  label = "Get Started",
  onClick,
  children,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [ripples, setRipples] = useState([]);
  const buttonRef = useRef(null);
  const rippleId = useRef(0);

  // Inject styles for shimmer and ripples
  useEffect(() => {
    const styleId = "liquid-glass-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes ripple-pop {
          0% { transform: translate(-50%, -50%) scale(0); opacity: 0.6; }
          100% { transform: translate(-50%, -50%) scale(4); opacity: 0; }
        }
        @keyframes shimmer-move {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .css-shimmer {
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.4) 30%,
            rgba(255, 255, 255, 0.8) 50%,
            rgba(255, 255, 255, 0.4) 70%,
            rgba(255, 255, 255, 0) 100%
          );
          background-size: 200% auto;
          mix-blend-mode: overlay;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsPressed(false);
  };
  const handleClick = (e) => {
    // Ripple
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const ripple = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        id: rippleId.current++,
      };
      setRipples((prev) => [...prev, ripple]);
      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== ripple.id));
      }, 600);
    }
    onClick?.();
  };

  const displayText = children || label;

  return (
    <div className="relative inline-block">
      {/* Invisible SVG filter for glass distortion */}
      <GlassFilter />

      <button
        ref={buttonRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        className="relative inline-flex items-center justify-center cursor-pointer h-11 px-6 py-2 rounded-full text-sm font-medium text-white whitespace-nowrap outline-none"
        style={{
          transform: isPressed
            ? "translateY(1px) scale(0.97)"
            : "translateY(0) scale(1)",
          transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        aria-label={typeof displayText === "string" ? displayText : label}
      >
        {/* ── Layer 1: Glass shadow ring ── */}
        <div
          className="absolute top-0 left-0 z-0 h-full w-full rounded-full transition-all"
          style={{
            boxShadow: isHovered
              ? "0 0 8px rgba(0,0,0,0.03), 0 2px 6px rgba(0,0,0,0.08), inset 3px 3px 0.5px -3.5px rgba(255,255,255,0.12), inset -3px -3px 0.5px -3.5px rgba(255,255,255,0.9), inset 1px 1px 1px -0.5px rgba(255,255,255,0.7), inset -1px -1px 1px -0.5px rgba(255,255,255,0.7), inset 0 0 6px 6px rgba(255,255,255,0.15), inset 0 0 2px 2px rgba(255,255,255,0.08), 0 0 16px rgba(0,0,0,0.2)"
              : "0 0 8px rgba(0,0,0,0.03), 0 2px 6px rgba(0,0,0,0.08), inset 3px 3px 0.5px -3.5px rgba(255,255,255,0.09), inset -3px -3px 0.5px -3.5px rgba(255,255,255,0.85), inset 1px 1px 1px -0.5px rgba(255,255,255,0.6), inset -1px -1px 1px -0.5px rgba(255,255,255,0.6), inset 0 0 6px 6px rgba(255,255,255,0.12), inset 0 0 2px 2px rgba(255,255,255,0.06), 0 0 12px rgba(0,0,0,0.15)",
          }}
        />

        {/* ── Layer 2: Glass distortion backdrop ── */}
        <div
          className="absolute top-0 left-0 isolate -z-10 h-full w-full overflow-hidden rounded-full"
          style={{ backdropFilter: 'url("#liquid-glass-filter")' }}
        />

        {/* ── Layer 3: Pure CSS Shimmer overlay ── */}
        <div
          className="absolute inset-0 rounded-full overflow-hidden pointer-events-none"
          style={{ zIndex: 1 }}
        >
          <div
            className="css-shimmer absolute inset-0"
            style={{
              animation: isHovered
                ? "shimmer-move 1.5s infinite linear"
                : "shimmer-move 4s infinite linear",
              opacity: isHovered ? 0.7 : 0.3,
              transition: "opacity 0.3s ease",
            }}
          />
        </div>

        {/* ── Layer 4: Text content ── */}
        <span className="pointer-events-none z-10 relative">
          {displayText}
        </span>

        {/* ── Ripple effects ── */}
        {ripples.map((ripple) => (
          <span
            key={ripple.id}
            className="pointer-events-none absolute"
            style={{
              left: `${ripple.x}px`,
              top: `${ripple.y}px`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)",
              animation: "ripple-pop 0.6s ease-out",
              zIndex: 20,
            }}
          />
        ))}
      </button>
    </div>
  );
}

/** SVG filter that creates the frosted glass distortion effect */
function GlassFilter() {
  return (
    <svg className="hidden" aria-hidden="true">
      <defs>
        <filter
          id="liquid-glass-filter"
          x="0%"
          y="0%"
          width="100%"
          height="100%"
          colorInterpolationFilters="sRGB"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.05 0.05"
            numOctaves="1"
            seed="1"
            result="turbulence"
          />
          <feGaussianBlur
            in="turbulence"
            stdDeviation="2"
            result="blurredNoise"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="blurredNoise"
            scale="70"
            xChannelSelector="R"
            yChannelSelector="B"
            result="displaced"
          />
          <feGaussianBlur
            in="displaced"
            stdDeviation="4"
            result="finalBlur"
          />
          <feComposite
            in="finalBlur"
            in2="finalBlur"
            operator="over"
          />
        </filter>
      </defs>
    </svg>
  );
}
